/* eslint-disable @stylistic/max-len */
import React, {type RefObject} from 'react';

interface ArrowRightProps {
    /**
     * A string representing the CSS class to be applied to the ArrowRightIcon element to override its default styling.
     */
    className?: string;
    /**
     * A string representing the color to be applied.
     */
    color1?: string;
    /**
     * A number or string representing the height of the ArrowRightIcon element.
     */
    height?: number | string;
    /**
     * The `ref` property represents a reference to the SVG element, allowing for direct manipulation or access to the DOM node.
     */
    ref?: RefObject<SVGSVGElement | null>;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     */
    testId?: string;
    /**
     * A number or string representing the height of the ArrowRightIcon element.
     */
    width?: number | string;
}

/**
 * The `ArrowRight` component is a functional component designed to render a customizable SVG element representing an Icon.
 * It receives several props through the `ArrowRightProps` interface, allowing for customization of class, color, dimensions, and testing identifier.
 *
 * @param props           The component props.
 * @param props.className A string representing the CSS class to be applied to the ArrowRightIcon element to override its default styling.
 * @param props.color1    A string representing the color to be applied to the Icon.
 * @param props.height    A number or string representing the height of the ArrowRightIcon element.
 * @param props.ref       A reference to the SVG element, allowing for direct manipulation or access to the DOM node.
 * @param props.testId    A string representing a unique identifier assigned to the component for testing purposes.
 * @param props.width     A number or string representing the height of the ArrowRightIcon element.
 * @returns A React element representing the `Check` component with the specified properties.
 *
 * @example
 * ```tsx
 * const ArrowRightComponent = <ArrowRight className="myClass" color1="#000" height="20" testId="myTestId" width="20" />;
 * ```
 */
export const ArrowRight = ({className = '', color1 = '#212325', height = 49, ref, testId = 'ArrowRight', width = 50}: ArrowRightProps) => (
    <svg
        ref={ref}
        className={className}
        data-cy={testId}
        height={height}
        viewBox="0 0 50 49"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
    >
        <path d="M10.9453 24.4888H39.5167M39.5167 24.4888L25.231 10.2031M39.5167 24.4888L25.231 38.7746" stroke={color1} strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.04082" />
    </svg>
);

ArrowRight.displayName = 'ArrowRight';